import { Check, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { PasswordInput } from "../ui/password-input";
import { Label } from "../ui/label";
import { passwordIssues, PASSWORD_MIN, type PasswordIssue } from "../../../../shared/account-rules";
import { cn } from "../../lib/utils";

/**
 * 新密碼＋確認密碼（帳號管理批次共用：改密碼／強制改密碼／重設密碼／管理員建帳號）。
 * 規則即時顯示，與後端 shared/account-rules 同一套。
 */
export function newPasswordValid(pw: string, confirm: string): boolean {
  return passwordIssues(pw).length === 0 && pw === confirm;
}

const RULES: Array<{ key: PasswordIssue; label: { "zh-TW": string; "en-US": string; "zh-CN": string } }> = [
  { key: "too_short", label: { "zh-TW": `至少 ${PASSWORD_MIN} 個字元`, "en-US": `At least ${PASSWORD_MIN} characters`, "zh-CN": `至少 ${PASSWORD_MIN} 个字元` } },
  { key: "need_letter", label: { "zh-TW": "至少一個英文字母", "en-US": "At least one letter", "zh-CN": "至少一个英文字母" } },
  { key: "need_digit", label: { "zh-TW": "至少一個數字", "en-US": "At least one digit", "zh-CN": "至少一个数字" } },
];

export function PasswordFields({
  password,
  confirm,
  onPassword,
  onConfirm,
  idPrefix = "pw",
  autoFocus,
}: {
  password: string;
  confirm: string;
  onPassword: (v: string) => void;
  onConfirm: (v: string) => void;
  idPrefix?: string;
  autoFocus?: boolean;
}) {
  const { t } = useLanguage();
  const issues = passwordIssues(password);
  const mismatch = confirm !== "" && confirm !== password;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-new`} className="text-[12px]">
          {t({ "zh-TW": "新密碼", "en-US": "New password", "zh-CN": "新密码" })}
        </Label>
        <PasswordInput
          id={`${idPrefix}-new`}
          autoComplete="new-password"
          autoFocus={autoFocus}
          value={password}
          onChange={(e) => onPassword(e.target.value)}
        />
        <ul className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5 text-[12px]">
          {RULES.map((r) => {
            const ok = password !== "" && !issues.includes(r.key);
            return (
              <li key={r.key} className={cn("flex items-center gap-1", ok ? "text-[var(--color-success)]" : "text-muted-foreground")}>
                {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3 opacity-50" />}
                {t(r.label)}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-confirm`} className="text-[12px]">
          {t({ "zh-TW": "確認新密碼", "en-US": "Confirm new password", "zh-CN": "确认新密码" })}
        </Label>
        <PasswordInput
          id={`${idPrefix}-confirm`}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => onConfirm(e.target.value)}
          aria-invalid={mismatch || undefined}
        />
        {mismatch && (
          <p className="text-[12px] text-destructive">
            {t({ "zh-TW": "兩次輸入的密碼不一致。", "en-US": "Passwords do not match.", "zh-CN": "两次输入的密码不一致。" })}
          </p>
        )}
      </div>
    </div>
  );
}
